import { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  X,
  Upload,
  Download,
  AlertCircle,
  CheckCircle,
  FileSpreadsheet,
  Eye,
  Save,
  Package
} from 'lucide-react';
import projectTypeService, { ProjectType } from '../../../services/projectTypeService';

// Sorigin Brand Colors [[memory:5323299]]
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  accent: "#A3CD39",
  dark: "#007F66",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
  info: "#3B82F6",
  infoLight: "#EFF6FF",
};

interface ProjectBulkUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ProjectPreview {
  projectName: string;
  projectTypeId: string;
  projectTypeName: string;
  location: string;
  description?: string;
  status: 'active' | 'inactive';
  startDate?: string;
  endDate?: string;
  projectManager?: string;
  clientName?: string;
  budget?: number;
  rowIndex: number;
  errors: string[];
  isValid: boolean;
}

interface UploadStats {
  total: number;
  valid: number;
  invalid: number;
  errors: string[];
}

export default function ProjectBulkUploadModal({
  open,
  onClose,
  onSuccess
}: ProjectBulkUploadModalProps) {
  const [step, setStep] = useState<'template' | 'upload' | 'preview' | 'processing'>('template');
  const [projects, setProjects] = useState<ProjectPreview[]>([]);
  const [uploadStats, setUploadStats] = useState<UploadStats>({ total: 0, valid: 0, invalid: 0, errors: [] });
  const [processing, setProcessing] = useState(false);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [loadingProjectTypes, setLoadingProjectTypes] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load project types on component mount
  useEffect(() => {
    if (open) {
      loadProjectTypes();
    }
  }, [open]);

  const loadProjectTypes = async () => {
    setLoadingProjectTypes(true);
    try {
      const response = await projectTypeService.getProjectTypes({ status: 'active', limit: 100 });
      if (response.success && response.data) {
        setProjectTypes(response.data.docs || response.data);
      }
    } catch (error) {
      console.error('Failed to load project types:', error);
      toast.error('Failed to load project types');
    } finally {
      setLoadingProjectTypes(false);
    }
  };

  // Template structure
  const templateColumns = [
    'projectName',
    'projectType',
    'location',
    'description',
    'status',
    'startDate',
    'endDate',
    'projectManager',
    'clientName',
    'budget'
  ];

  const getTemplateData = () => {
    // Use actual project types if available, otherwise use sample
    const sampleProjectType = projectTypes.length > 0 ? projectTypes[0].name : 'Solar Ground Mounted';
    const sampleProjectType2 = projectTypes.length > 1 ? projectTypes[1].name : 'Wind Turbine';
    
    return [
      {
        projectName: 'Solar Farm Installation - Phase 1',
        projectType: sampleProjectType,
        location: 'Mumbai',
        description: 'Large-scale solar farm installation with 100MW capacity',
        status: 'active',
        startDate: '2024-01-15',
        endDate: '2024-06-30',
        projectManager: 'John Smith',
        clientName: 'Green Energy Corp',
        budget: 50000000
      },
      {
        projectName: 'Wind Turbine Maintenance Project',
        projectType: sampleProjectType2,
        location: 'Delhi',
        description: 'Annual maintenance of wind turbines',
        status: 'active',
        startDate: '2024-02-01',
        endDate: '2024-02-28',
        projectManager: 'Sarah Johnson',
        clientName: 'Wind Power Ltd',
        budget: 2500000
      }
    ];
  };

  const handleClose = () => {
    setStep('template');
    setProjects([]);
    setUploadStats({ total: 0, valid: 0, invalid: 0, errors: [] });
    setProcessing(false);
    onClose();
  };

  const downloadTemplate = () => {
    try {
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Get available project types for instructions
      const availableProjectTypes = projectTypes.map(pt => pt.name).join(', ') || 'Loading...';
      
      // Create instructions sheet
      const instructions = [
        ['PROJECT BULK UPLOAD TEMPLATE'],
        [''],
        ['INSTRUCTIONS:'],
        ['1. Fill in the data in the "Data" sheet below'],
        ['2. Required fields: projectName, projectType, location, status'],
        ['3. Optional fields: description, startDate, endDate, projectManager, clientName, budget'],
        ['4. Date format: YYYY-MM-DD (e.g., 2024-01-15)'],
        ['5. Status values: active, inactive'],
        [`6. Available Project Types: ${availableProjectTypes}`],
        ['7. Location must be from predefined list: Mumbai, Delhi, Bangalore, Chennai, Kolkata, Hyderabad, Pune'],
        ['8. Do not modify the column headers'],
        ['9. Remove this instructions sheet before uploading'],
        [''],
        ['VALIDATION RULES:'],
        ['- Project names must be unique and not empty'],
        ['- All required fields must be filled'],
        ['- Project type must match exactly from available types'],
        ['- Dates must be in correct format'],
        ['- Budget must be a positive number'],
        ['- Status must be either "active" or "inactive"'],
        ['- Location must be from predefined list'],
        [''],
        ['ERROR HANDLING:'],
        ['- Invalid rows will be highlighted and skipped'],
        ['- Valid rows will be processed successfully'],
        ['- Detailed error messages will be shown for each issue']
      ];
      
      const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
      wsInstructions['!cols'] = [{ wch: 80 }]; // Set column width
      XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');
      
      // Create data sheet with sample data
      const templateData = getTemplateData();
      const wsData = XLSX.utils.json_to_sheet(templateData);
      wsData['!cols'] = [
        { wch: 35 }, // projectName
        { wch: 25 }, // projectType
        { wch: 15 }, // location
        { wch: 40 }, // description
        { wch: 10 }, // status
        { wch: 12 }, // startDate
        { wch: 12 }, // endDate
        { wch: 20 }, // projectManager
        { wch: 20 }, // clientName
        { wch: 15 }  // budget
      ];
      XLSX.utils.book_append_sheet(wb, wsData, 'Data');
      
      // Download file
      XLSX.writeFile(wb, 'Project_Bulk_Upload_Template.xlsx');
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const validateProject = (project: any, rowIndex: number): ProjectPreview => {
    const errors: string[] = [];
    
    // Required field validation
    if (!project.projectName || project.projectName.trim() === '') {
      errors.push('Project name is required');
    }
    
    if (!project.projectType || project.projectType.trim() === '') {
      errors.push('Project type is required');
    }
    
    if (!project.location || project.location.trim() === '') {
      errors.push('Location is required');
    }
    
    if (!project.status || project.status.trim() === '') {
      errors.push('Status is required');
    }
    
    // Find matching project type
    let matchedProjectType: ProjectType | undefined;
    let projectTypeId = '';
    let projectTypeName = '';
    
    if (project.projectType && project.projectType.trim() !== '') {
      matchedProjectType = projectTypes.find(pt => 
        pt.name.toLowerCase() === project.projectType.toLowerCase().trim()
      );
      
      if (matchedProjectType) {
        projectTypeId = matchedProjectType._id || matchedProjectType.id || '';
        projectTypeName = matchedProjectType.name;
      } else {
        const availableTypes = projectTypes.map(pt => pt.name).join(', ');
        errors.push(`Invalid project type "${project.projectType}". Available types: ${availableTypes}`);
        projectTypeName = project.projectType; // Keep original for display
      }
    }
    
    const validStatuses = ['active', 'inactive'];
    if (project.status && !validStatuses.includes(project.status)) {
      errors.push(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
    }
    
    const validLocations = ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad', 'Pune'];
    if (project.location && !validLocations.includes(project.location)) {
      errors.push(`Invalid location. Must be one of: ${validLocations.join(', ')}`);
    }
    
    // Date validation
    if (project.startDate && project.startDate.trim() !== '') {
      const startDate = new Date(project.startDate);
      if (isNaN(startDate.getTime())) {
        errors.push('Invalid start date format. Use YYYY-MM-DD');
      }
    }
    
    if (project.endDate && project.endDate.trim() !== '') {
      const endDate = new Date(project.endDate);
      if (isNaN(endDate.getTime())) {
        errors.push('Invalid end date format. Use YYYY-MM-DD');
      }
    }
    
    // Date range validation
    if (project.startDate && project.endDate && project.startDate.trim() !== '' && project.endDate.trim() !== '') {
      const startDate = new Date(project.startDate);
      const endDate = new Date(project.endDate);
      if (startDate >= endDate) {
        errors.push('End date must be after start date');
      }
    }
    
    // Budget validation
    if (project.budget && project.budget !== '') {
      const budget = parseFloat(project.budget);
      if (isNaN(budget) || budget < 0) {
        errors.push('Budget must be a positive number');
      }
    }
    
    return {
      projectName: project.projectName || '',
      projectTypeId,
      projectTypeName,
      location: project.location || '',
      description: project.description || '',
      status: project.status || 'active',
      startDate: project.startDate || '',
      endDate: project.endDate || '',
      projectManager: project.projectManager || '',
      clientName: project.clientName || '',
      budget: project.budget ? parseFloat(project.budget) : undefined,
      rowIndex,
      errors,
      isValid: errors.length === 0
    };
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first worksheet (assuming data is in the first sheet)
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (jsonData.length === 0) {
          toast.error('No data found in the Excel file');
          return;
        }

        // Validate each row
        const validatedProjects: ProjectPreview[] = [];
        const errors: string[] = [];
        let validCount = 0;
        let invalidCount = 0;

        jsonData.forEach((row: any, index: number) => {
          const validatedProject = validateProject(row, index + 2); // +2 because Excel is 1-indexed and we have headers
          validatedProjects.push(validatedProject);
          
          if (validatedProject.isValid) {
            validCount++;
          } else {
            invalidCount++;
            errors.push(`Row ${validatedProject.rowIndex}: ${validatedProject.errors.join(', ')}`);
          }
        });

        // Check for duplicate project names
        const projectNames = validatedProjects.map(p => p.projectName.toLowerCase().trim());
        const duplicates = projectNames.filter((name, index) => projectNames.indexOf(name) !== index);
        
        if (duplicates.length > 0) {
          const duplicateNames = [...new Set(duplicates)];
          errors.push(`Duplicate project names found: ${duplicateNames.join(', ')}`);
          
          // Mark duplicates as invalid
          validatedProjects.forEach(project => {
            if (duplicateNames.includes(project.projectName.toLowerCase().trim())) {
              project.errors.push('Duplicate project name');
              project.isValid = false;
            }
          });
          
          validCount = validatedProjects.filter(p => p.isValid).length;
          invalidCount = validatedProjects.length - validCount;
        }

        setProjects(validatedProjects);
        setUploadStats({
          total: validatedProjects.length,
          valid: validCount,
          invalid: invalidCount,
          errors
        });
        
        setStep('preview');
        
        if (validCount > 0) {
          toast.success(`File processed successfully. ${validCount} valid projects found.`);
        }
        
        if (invalidCount > 0) {
          toast.warning(`${invalidCount} projects have validation errors. Please review the preview.`);
        }

      } catch (error) {
        console.error('Error processing file:', error);
        toast.error('Failed to process Excel file. Please check the format.');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async () => {
    const validProjects = projects.filter(p => p.isValid);
    
    if (validProjects.length === 0) {
      toast.error('No valid projects to upload');
      return;
    }

    setProcessing(true);
    setStep('processing');

    try {
      let successCount = 0;
      const errors: string[] = [];

      // Process each valid project
      for (const [index, project] of validProjects.entries()) {
        try {
          const projectData = {
            projectName: project.projectName.trim(),
            projectTypeId: project.projectTypeId,
            location: project.location,
            description: project.description || '',
            status: project.status,
            startDate: project.startDate || undefined,
            endDate: project.endDate || undefined,
            projectManager: project.projectManager || '',
            clientName: project.clientName || '',
            budget: project.budget || undefined,
          };

          // TODO: Replace with actual API call
          // await projectService.createProject(projectData);
          
          // Simulate API call for now
          await new Promise(resolve => setTimeout(resolve, 100));
          
          successCount++;
          console.log(`✅ Created project ${index + 1}/${validProjects.length}: ${project.projectName}`);
          
        } catch (error: any) {
          console.error(`❌ Failed to create project ${project.projectName}:`, error);
          const errorMessage = error?.message || 'Unknown error';
          errors.push(`${project.projectName}: ${errorMessage}`);
        }
      }

      // Show results
      if (successCount > 0) {
        toast.success(`Successfully created ${successCount} projects`);
        onSuccess();
      }
      
      if (errors.length > 0) {
        console.error('Project creation errors:', errors);
        toast.error(`Failed to create ${errors.length} projects. First error: ${errors[0]}`);
        
        // If some succeeded but some failed, don't close the modal
        if (successCount === 0) {
          setStep('preview');
          return;
        }
      }
      
      // Close modal only if all succeeded or if some succeeded (partial success)
      if (successCount > 0) {
        handleClose();
      }
      
    } catch (error: any) {
      console.error('Submission error:', error);
      toast.error(error?.message || 'Failed to create projects');
      setStep('preview');
    } finally {
      setProcessing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shadow-sm"
                style={{ 
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                  color: 'white'
                }}
              >
                <Package size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Project Bulk Upload</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {step === 'template' && 'Download template and prepare your data'}
                  {step === 'upload' && 'Upload your Excel file'}
                  {step === 'preview' && `Preview ${uploadStats.valid} valid projects`}
                  {step === 'processing' && 'Creating projects...'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close"
              disabled={processing}
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {step === 'template' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Download Template
                </h3>
                <p className="text-sm text-gray-600">
                  Download the Excel template and fill in your project data
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FileSpreadsheet className="w-8 h-8 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-2">Template Features:</h4>
                    <ul className="text-blue-800 space-y-1 text-sm">
                      <li>• Pre-formatted columns with proper headers</li>
                      <li>• Sample data using actual project types from your system</li>
                      <li>• Built-in validation rules for all fields</li>
                      <li>• Instructions sheet with detailed guidelines</li>
                      <li>• Support for all project fields including project types</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-yellow-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-yellow-900 mb-2">Important Notes:</h4>
                    <ul className="text-yellow-800 space-y-1 text-sm">
                      <li>• Required fields: projectName, projectType, location, status</li>
                      <li>• Project type must match exactly from available types in your system</li>
                      <li>• Date format: YYYY-MM-DD (e.g., 2024-01-15)</li>
                      <li>• Status values: active, inactive</li>
                      <li>• Project names must be unique</li>
                      <li>• Remove the instructions sheet before uploading</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={downloadTemplate}
                  className="inline-flex items-center gap-3 px-6 py-3 text-sm font-medium text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  style={{ 
                    background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
                  }}
                >
                  <Download size={20} />
                  Download Template
                </button>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">Once you've filled the template, upload it here:</p>
                <button
                  onClick={() => setStep('upload')}
                  className="inline-flex items-center gap-3 px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
                >
                  <Upload size={20} />
                  Upload Excel File
                </button>
              </div>
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Upload Excel File
                </h3>
                <p className="text-sm text-gray-600">
                  Select your filled Excel template to upload
                </p>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">Drop your Excel file here</h4>
                <p className="text-sm text-gray-600 mb-4">
                  or click to browse and select your file
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white rounded-lg transition-all duration-200"
                  style={{ backgroundColor: BRAND_COLORS.primary }}
                >
                  <Upload size={16} />
                  Choose File
                </button>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep('template')}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back to Template
                </button>
                <button
                  onClick={() => setStep('template')}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Preview Projects ({uploadStats.total} total)
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setStep('upload')}
                    className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Upload Different File
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-900">{uploadStats.total}</div>
                  <div className="text-sm text-blue-700">Total Projects</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-900">{uploadStats.valid}</div>
                  <div className="text-sm text-green-700">Valid Projects</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-900">{uploadStats.invalid}</div>
                  <div className="text-sm text-red-700">Invalid Projects</div>
                </div>
              </div>

              {/* Projects List */}
              <div className="max-h-96 overflow-y-auto space-y-3">
                {projects.map((project, index) => (
                  <div key={index} className={`border rounded-lg p-4 ${
                    project.isValid 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-red-200 bg-red-50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                            project.isValid 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {index + 1}
                          </span>
                          <h4 className="font-medium text-gray-900">{project.projectName}</h4>
                          <div className="flex items-center gap-2">
                            {project.isValid ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-600" />
                            )}
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              project.isValid 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {project.isValid ? 'Valid' : 'Invalid'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600">
                          <div><strong>Type:</strong> {project.projectTypeName}</div>
                          <div><strong>Location:</strong> {project.location}</div>
                          <div><strong>Status:</strong> {project.status}</div>
                        </div>
                        
                        {project.errors.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-red-700 mb-1">Errors:</p>
                            <ul className="text-xs text-red-600 space-y-1">
                              {project.errors.map((error, errorIndex) => (
                                <li key={errorIndex}>• {error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary Errors */}
              {uploadStats.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2">Summary of Errors:</h4>
                  <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                    {uploadStats.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setStep('upload')}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={uploadStats.valid === 0}
                    className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: BRAND_COLORS.primary }}
                  >
                    <Save size={16} />
                    Create {uploadStats.valid} Projects
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4">
                  <div className="w-16 h-16 border-4 border-gray-200 border-t-green-500 rounded-full animate-spin"></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Creating Projects
                </h3>
                <p className="text-sm text-gray-600">
                  Please wait while we create your projects...
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-blue-600" />
                  <div className="text-sm text-blue-800">
                    Processing {uploadStats.valid} valid projects. This may take a few moments.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
